// src/types/Helper/Enums.ts

export enum CancellationReasons {
  Weather = 'Weather',
  Maint = 'Maintenance',
  IU = 'Instructor Unavailable',
  Other = 'Other'
}

export enum Resource {
  ac = 'Aircraft',
  room = 'Rooms',
  ins = 'Instructor',
  acIns = 'Aircraft and Instructors',
  roomIns = 'Rooms and Instructors'
}

export enum AdvancedEquipment {
  gps1 = 'Gps 1 Database',
  gps2 = 'Gps 2 Database',
  avidyne = 'Avidyne',
  none = 'None'
}

export enum FormComponent {
  CheckIn = 'Check In',
  CheckOut = 'Check Out',
  CreateEvent = 'Create Event',
  CreateCustomer = 'Create Customer',
  CreateAircraft = 'Create Aircraft',
  Squawk = 'Squawk'
}

export enum Roles {
  User = 'User',
  Student = 'Student',
  Renter = 'Renter',
  IntlStudent = 'International student',
  Employee = 'Employee',
  Dispatch = 'Dispatcher' /* aircraft dispatcher mainly 141 */,
  Scheduler = 'Scheduler' /* For scheduling privledges only */,
  Finance = 'Finance' /* For accountant */,
  Admin = 'Admin',
  Maintenance = 'Maintenance Tech' /* Creates and edits UNSIGNED maintenance entries */,
  // Mechanic certificates — who may SIGN maintenance entries (see
  // utils/signingPermissions.ts). Their credentials suffix on a signed entry
  // comes from these roles: A, P, AP or IA.
  MechanicA = 'Mechanic-A' /* Airframe: signs airframe and "other" entries */,
  MechanicP = 'Mechanic-P' /* Powerplant: signs engine and propeller entries */,
  MechanicAP = 'Mechanic-AP' /* Airframe & Powerplant: signs all but Annuals */,
  IA = 'IA' /* Inspection Authorization: signs Annuals, and everything an AP signs */,
  FuelAdmin = 'Fuel Admin' /* Full fueling module access — trucks, prices, reports, deletions */,
  Fueler = 'Fueler' /* Fuel entry only */,
  HeadInstructor = 'Head Instructor',
  Instructor = 'Instructor', // Affiliated instructor with flight school. Has full instructor privledges.
  CFI = 'CFI' /* Certified Flight Instructor, but dosen't have full instructor privileges */,
  CFII = 'CFII' /* Certified Flight Instructor - Instrument, but dosen't have full instructor privileges*/,
  MEI = 'MEI' /* Multi-Engine Instructor */,
  Part61 = 'Part 61',
  Part141 = 'Part 141',
  faa = 'FAA',
  DPE = 'DPE' /* Designated Pilot Examiner occupation; personal service access uses DPE profiles. */,
  ChiefPilot = 'Chief Pilot',
  asstchiefPilot = 'Assistant Chief Pilot',
  chiefInstructor = 'Chief Instructor',
  Dev = 'Dev',
  AircraftOwner = 'Aircraft Owner',
  StoreOwner = 'Owner' /* Persisted store-owner role; has full privileges */,
  RequiresDispatch = 'Requires Dispatch' /* pilot must be dispatched by a Dispatcher before checkout */
}

export const RoleLabels: Partial<Record<Roles, string>> = {
  [Roles.Instructor]: 'Instructor - Affiliated'
}

export function getRoleLabel(role: Roles): string {
  return RoleLabels[role] ?? role
}

export enum Category {
  Airplane = 'Airplane',
  Glider = 'Glider',
  Rotorcraft = 'Rotorcraft',
  LighterThanAir = 'Lighter than Air',
  PoweredLift = 'Powered Lift',
  PoweredParachute = 'Powered Parachute',
  WeightShiftControl = 'Weight Shift Control',
  Other = 'Other'
}

export enum CurrencyType {
  None = 'None',
  MakeModel = 'Make/Model',
  Category = 'Category',
  Class = 'Class',
  Exact = 'Exact'
}

export enum Airplane {
  ses = 'Single-Engine Sea',
  sel = 'Single-Engine Land',
  mes = 'Multi-Engine Sea',
  mel = 'Multi-Engine Land'
}

export enum Environment {
  Land,
  Sea,
  Amphibian
}

export enum EngineType {
  None,
  Reciprocating,
  Turboprop,
  Turboshaft,
  Turbojet,
  Turbofan,
  Ramjet,
  TwoCycle,
  FourCycle,
  Unknown,
  Electric,
  Rotary
}

export enum Glider {
  AeroTow,
  GroundTow,
  SelfLaunch
}

export enum Rotorcraft {
  Helicopter,
  'R22-Sfar73',
  'R44-Sfar73',
  Gyroplane
}

export enum LighterThanAir {
  Airship,
  GasBalloon,
  HeaterBalloon
}

export enum PoweredLift {}

export enum PoweredParachute {}

export enum WeightShiftControl {}

export enum AircraftType {
  pres = 'Pressurization',
  hA = 'High Altitude',
  hP = 'High Performance',
  complex = 'Complex',
  inst = 'Instrument',
  tailWheel = 'Tail Wheel',
  taa = 'Technically Advanced Aircraft',
  constspd = 'Constant Speed',
  std = 'Standard' //standard aircraft, none of the above
}

export enum membershipType {
  monthly = 'Monthly',
  yearly = 'Yearly',
  none = 'None',
  employee = 'Employee',
  lifetime = 'Lifetime',
  noexpiration = 'No Expiration',
  other = 'Other',
  inactive = 'Inactive'
}

export const PRICED_MEMBERSHIP_TYPES = [
  membershipType.monthly,
  membershipType.yearly,
  membershipType.employee,
  membershipType.other
] as const

export type PricedMembershipType = (typeof PRICED_MEMBERSHIP_TYPES)[number]

export enum FaaAircraftTypes {
  Glider = 1,
  Balloon = 2,
  BlimpDirigible = 3,
  FixedWingSingle = 4,
  FixedWingMulti = 5,
  Rotorcraft = 6,
  WeightShiftControl = 7,
  PoweredParachute = 8,
  Gyroplane = 9,
  HybridLift = 'H',
  Other = 'O'
}

export enum typeAdminNotifications {
  Squawks = 'Squawks',
  MC = 'Maintenance Complete',
  Updated = 'Updated',
  Created = 'Created'
}

export enum typeMaintNotifications {
  Squawks = 'Squawks',
  MC = 'Maintenance Complete',
  Updated = 'Updated',
  Created = 'Created'
}

export enum EventCategory {
  training = 'Training',
  checkRide = 'Check-Ride',
  maintenance = 'Maintenance',
  flight = 'Flight'
}

export enum WageServiceType {
  Primary,
  Advanced,
  StageCheck,
  Other
}
